import React, { useState, useEffect, useCallback } from "react";
import {
  FiPlus,
  FiChevronDown,
  FiChevronRight,
  FiEdit2,
  FiTrash2,
  FiX,
  FiLoader,
  FiInbox,
  FiImage,
} from "react-icons/fi";
import { makeRequestAuth } from "../../_config/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { ToastContainer } from "react-toastify";

const CategoryQuezze = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openCategory, setOpenCategory] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [categoryName, setCategoryName] = useState("");
  const [totalQuizzes, setTotalQuizzes] = useState(0);
  const [subCategories, setSubCategories] = useState([{ name: "" }]);
  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await makeRequestAuth("post", "/admin/getQuizCategories", {});
      if (res?.code === 0) {
        setCategories(res.data || []);
      } else {
        showErrorToast(res?.message || "Failed to load categories");
      }
    } catch (error) {
      console.error("Fetch Error:", error);
      showErrorToast("Failed to load categories");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      name: categoryName,
      totalQuizzes: Number(totalQuizzes),
      subCategories: subCategories.filter(
        (sub) => sub.name && sub.name.trim() !== "",
      ),
    };

    try {
      let res;
      if (editingId) {
        res = await makeRequestAuth("post", "/admin/updateQuizCategory", {
          ...payload,
          categoryId: editingId,
        });
      } else {
        res = await makeRequestAuth("post", "/admin/addQuizCategory", payload);
      }

      if (res?.code === 0) {
        showSuccessToast(
          editingId
            ? "Category updated successfully"
            : "Category added successfully",
        );
        closeModal();
        fetchCategories();
      } else {
        showErrorToast(res?.message || "Operation failed");
      }
    } catch (error) {
      console.error("Submit Error:", error);
      showErrorToast("Something went wrong");
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this category?"))
      return;

    try {
      const res = await makeRequestAuth("post", "/admin/deleteQuizCategory", {
        categoryId: id,
      });

      if (res?.code === 0) {
        showSuccessToast("Category deleted successfully");
        fetchCategories();
      } else {
        showErrorToast(res?.message || "Delete failed");
      }
    } catch (error) {
      console.error("Delete Error:", error);
      showErrorToast("Unable to delete category");
    }
  };

  const openEditModal = (cat) => {
    setEditingId(cat._id);
    setCategoryName(cat.name);
    setTotalQuizzes(cat.totalQuizzes || 0);
    setSubCategories(
      cat.subCategories?.length
        ? cat.subCategories.map((s) => ({ name: s.name }))
        : [{ name: "" }],
    );
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setCategoryName("");
    setTotalQuizzes(0);
    setSubCategories([{ name: "" }]);
  };

  const toggleCategory = (id) => {
    setOpenCategory(openCategory === id ? null : id);
  };

  const handleSubChange = (index, value) => {
    const updatedSubs = [...subCategories];
    updatedSubs[index].name = value;
    setSubCategories(updatedSubs);
  };

  return (
    <div className="p-4">
      {/* Header Section */}
      <ToastContainer />
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 tracking-tight">
            Category Management
          </h1>
          <p className="text-sm text-slate-500">
            Organize and manage your quiz categories & sub-categories
          </p>
        </div>

        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 bg-[#2563eb] text-white px-5 py-2.5 rounded-lg hover:bg-blue-700 transition-all shadow-md shadow-blue-100 font-medium active:scale-95"
        >
          <FiPlus size={20} /> Create New Category
        </button>
      </div>

      {/* Main Content Area */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <FiLoader className="animate-spin text-blue-600 mb-4" size={40} />
          <p className="text-slate-500">Fetching categories...</p>
        </div>
      ) : categories.length === 0 ? (
        <div className="bg-white rounded-2xl border-2 border-dashed border-slate-200 p-20 flex flex-col items-center text-center">
          <FiInbox size={48} className="text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-slate-700">
            No Categories Found
          </h3>
          <p className="text-slate-500 mt-1">
            Start by adding a new quiz category.
          </p>
        </div>
      ) : (
        <div className="grid gap-3">
          {categories.map((cat) => {
            const isOpen = openCategory === cat._id;
            return (
              <div
                key={cat._id}
                className={`bg-white rounded-xl border transition-all duration-200 ${
                  isOpen
                    ? "border-blue-200 shadow-md"
                    : "border-slate-200 shadow-sm"
                }`}
              >
                {/* Category Row */}
                <div
                  onClick={() => toggleCategory(cat._id)}
                  className={`p-4 flex items-center justify-between cursor-pointer ${
                    isOpen ? "bg-blue-50/30" : "hover:bg-slate-50"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold ${
                        isOpen
                          ? "bg-blue-600 text-white"
                          : "bg-blue-100 text-blue-600"
                      }`}
                    >
                      {cat.name.charAt(0)}
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-800">{cat.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[11px] font-semibold bg-slate-100 text-slate-500 px-2 py-0.5 rounded uppercase">
                          {cat.subCategories?.length || 0} Sub-categories
                        </span>
                        <span className="text-[11px] font-semibold bg-blue-50 text-blue-600 px-2 py-0.5 rounded uppercase font-mono">
                          {cat.totalQuizzes || 0} Quizzes
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="flex mr-4 border-r pr-4 border-slate-200 gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditModal(cat);
                        }}
                        className="p-2 hover:bg-blue-50 rounded-lg text-slate-400 hover:text-blue-600 transition-colors"
                      >
                        <FiEdit2 size={18} />
                      </button>
                      <button
                        onClick={(e) => handleDelete(e, cat._id)}
                        className="p-2 hover:bg-red-50 rounded-lg text-slate-400 hover:text-red-500 transition-colors"
                      >
                        <FiTrash2 size={18} />
                      </button>
                    </div>
                    {isOpen ? (
                      <FiChevronDown size={22} className="text-blue-500" />
                    ) : (
                      <FiChevronRight size={22} className="text-slate-400" />
                    )}
                  </div>
                </div>

                {/* Sub-categories Dropdown */}
                {isOpen && (
                  <div className="bg-slate-50/50 px-6 md:px-20 py-5 border-t border-slate-100 animate-in slide-in-from-top-2 duration-200">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-4">
                      Sub Categories List
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {cat.subCategories?.length > 0 ? (
                        cat.subCategories.map((sub, index) => (
                          <div
                            key={index}
                            className="bg-white border border-slate-200 px-4 py-2 rounded-lg text-sm text-slate-600 shadow-sm font-medium"
                          >
                            {sub.name}
                          </div>
                        ))
                      ) : (
                        <p className="text-xs text-slate-400 italic">
                          No sub-categories available.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* --- ADD/EDIT MODAL --- */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center p-6 border-b border-slate-100 bg-slate-50/50">
              <h2 className="text-xl font-bold text-slate-800">
                {editingId ? "Update Category" : "New Category"}
              </h2>
              <button
                onClick={closeModal}
                className="text-slate-400 hover:text-slate-600 p-1 hover:bg-slate-100 rounded-full transition-colors"
              >
                <FiX size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-5">
                {/* Name */}
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    Category Name
                  </label>
                  <input
                    type="text"
                    required
                    value={categoryName}
                    onChange={(e) => setCategoryName(e.target.value)}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                    placeholder="e.g. SSC CGL"
                  />
                </div>

                {/* Total Quizzes */}
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    Total Quizzes
                  </label>
                  <input
                    type="number"
                    value={totalQuizzes}
                    onChange={(e) => setTotalQuizzes(e.target.value)}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                    placeholder="Enter total quizzes"
                  />
                </div>

                {/* Sub Categories Dynamic Fields */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-sm font-bold text-slate-700">
                      Sub Categories
                    </label>
                  </div>

                  <div className="space-y-3 max-h-[180px] overflow-y-auto pr-2 custom-scrollbar">
                    {subCategories.map((sub, index) => (
                      <div key={index} className="flex gap-2">
                        <input
                          type="text"
                          value={sub.name}
                          onChange={(e) =>
                            handleSubChange(index, e.target.value)
                          }
                          className="flex-1 px-4 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                          placeholder="Sub-category name"
                        />
                        {subCategories.length > 1 && (
                          <button
                            type="button"
                            onClick={() =>
                              setSubCategories(
                                subCategories.filter((_, i) => i !== index),
                              )
                            }
                            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                          >
                            <FiTrash2 size={18} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={() =>
                      setSubCategories([...subCategories, { name: "" }])
                    }
                    className="mt-3 flex items-center gap-1.5 text-xs font-bold text-blue-600 hover:underline"
                  >
                    <FiPlus size={14} /> ADD ANOTHER SUB
                  </button>
                </div>
              </div>

              <div className="mt-8 flex gap-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 rounded-xl font-bold hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all active:scale-[0.98]"
                >
                  {editingId ? "Update" : "Save"} Category
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryQuezze;
